// src/pages/Dashboard.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  Users,
  UserCheck,
  Calendar,
  CalendarDays,
  Key,
  Shield,
  CheckCircle2,
  Clock,
  UserPlus,
  KeyRound,
  BarChart3,
  PlusCircle,
  Edit,
  Trash2,
  ClipboardList,
} from "lucide-react";
import { fetchUserStats } from "../store/actions/userActions";
import { fetchRoles } from "../store/actions/roleActions";
import { fetchUsers } from "../store/actions/userActions";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Alert from "../components/common/Alert";

const Dashboard = () => {
  const dispatch = useDispatch();
  const { stats, loading, error } = useSelector((state) => state.users);
  const { roles } = useSelector((state) => state.roles);
  const [timeRange, setTimeRange] = useState("week");
  const [selectedChart, setSelectedChart] = useState("department");

  // Styles object
  const styles = {
    container: {
      maxWidth: "1280px",
      margin: "0 auto",
      padding: "2rem 1rem",
    },

    // Header
    header: {
      marginBottom: "2rem",
    },
    title: {
      fontSize: "1.875rem",
      fontWeight: 700,
      color: "#111827",
      margin: 0,
      marginBottom: "0.5rem",
    },
    subtitle: {
      fontSize: "0.875rem",
      color: "#6b7280",
      margin: 0,
    },

    // Stats Grid
    statsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
      gap: "1.5rem",
      marginBottom: "2rem",
    },
    statCard: {
      backgroundColor: "#ffffff",
      borderRadius: "0.5rem",
      boxShadow:
        "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
      padding: "1.5rem",
      transition: "all 0.2s",
      cursor: "pointer",
      textDecoration: "none",
      display: "block",
    },
    statCardHover: {
      transform: "translateY(-2px)",
      boxShadow:
        "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    },
    statContent: {
      display: "flex",
      alignItems: "center",
    },
    statIcon: {
      width: "3rem",
      height: "3rem",
      borderRadius: "0.5rem",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginRight: "1rem",
      fontSize: "1.5rem",
    },
    statIconBlue: {
      backgroundColor: "#dbeafe",
      color: "#1e40af",
    },
    statIconGreen: {
      backgroundColor: "#d1fae5",
      color: "#065f46",
    },
    statIconPurple: {
      backgroundColor: "#ede9fe",
      color: "#5b21b6",
    },
    statIconOrange: {
      backgroundColor: "#fed7aa",
      color: "#92400e",
    },
    statIconRed: {
      backgroundColor: "#fee2e2",
      color: "#991b1b",
    },
    statIconIndigo: {
      backgroundColor: "#e0e7ff",
      color: "#3730a3",
    },
    statIconTeal: {
      backgroundColor: "#ccfbf1",
      color: "#115e59",
    },
    statIconPink: {
      backgroundColor: "#fce7f3",
      color: "#9d174d",
    },
    statInfo: {
      flex: 1,
    },
    statLabel: {
      fontSize: "0.875rem",
      fontWeight: 500,
      color: "#6b7280",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      marginBottom: "0.25rem",
    },
    statValue: {
      fontSize: "1.5rem",
      fontWeight: 700,
      color: "#111827",
      lineHeight: 1.2,
    },
    statChange: {
      fontSize: "0.875rem",
      marginTop: "0.25rem",
    },
    statChangePositive: {
      color: "#059669",
    },
    statChangeNegative: {
      color: "#dc2626",
    },

    // Charts Grid
    chartsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      gap: "1.5rem",
      marginBottom: "2rem",
    },
    chartCard: {
      backgroundColor: "#ffffff",
      borderRadius: "0.5rem",
      boxShadow:
        "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
      padding: "1.5rem",
    },
    chartHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "1rem",
    },
    chartTitle: {
      fontSize: "1.125rem",
      fontWeight: 600,
      color: "#111827",
      margin: 0,
    },
    chartControls: {
      display: "flex",
      gap: "0.5rem",
    },
    chartControlButton: {
      padding: "0.25rem 0.75rem",
      fontSize: "0.75rem",
      fontWeight: 500,
      backgroundColor: "#f3f4f6",
      color: "#4b5563",
      border: "none",
      borderRadius: "0.25rem",
      cursor: "pointer",
      transition: "all 0.2s",
    },
    chartControlButtonActive: {
      backgroundColor: "#2563eb",
      color: "#ffffff",
    },
    chartControlButtonHover: {
      backgroundColor: "#e5e7eb",
    },

    // Bar Chart
    barChart: {
      marginTop: "1rem",
    },
    barItem: {
      marginBottom: "0.75rem",
    },
    barLabel: {
      display: "flex",
      justifyContent: "space-between",
      marginBottom: "0.25rem",
      fontSize: "0.875rem",
    },
    barLabelText: {
      color: "#4b5563",
    },
    barLabelValue: {
      fontWeight: 500,
      color: "#111827",
    },
    barContainer: {
      width: "100%",
      height: "0.5rem",
      backgroundColor: "#e5e7eb",
      borderRadius: "0.25rem",
      overflow: "hidden",
    },
    barFill: {
      height: "100%",
      borderRadius: "0.25rem",
      transition: "width 0.3s ease-out",
    },
    barFillBlue: {
      backgroundColor: "#3b82f6",
    },
    barFillGreen: {
      backgroundColor: "#10b981",
    },
    barFillPurple: {
      backgroundColor: "#8b5cf6",
    },
    barFillOrange: {
      backgroundColor: "#f59e0b",
    },
    barFillRed: {
      backgroundColor: "#ef4444",
    },

    // Pie Chart (simplified with circles)
    pieChartContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-around",
      marginTop: "1rem",
    },
    pieChart: {
      position: "relative",
      width: "150px",
      height: "150px",
      borderRadius: "50%",
      background:
        "conic-gradient(#3b82f6 0deg 120deg, #10b981 120deg 220deg, #8b5cf6 220deg 300deg, #f59e0b 300deg 360deg)",
    },
    pieChartCenter: {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: "60px",
      height: "60px",
      backgroundColor: "#ffffff",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "1rem",
      fontWeight: 600,
      color: "#111827",
    },
    pieLegend: {
      flex: 1,
      marginLeft: "1.5rem",
    },
    legendItem: {
      display: "flex",
      alignItems: "center",
      marginBottom: "0.5rem",
    },
    legendColor: {
      width: "0.75rem",
      height: "0.75rem",
      borderRadius: "0.25rem",
      marginRight: "0.5rem",
    },
    legendColorBlue: {
      backgroundColor: "#3b82f6",
    },
    legendColorGreen: {
      backgroundColor: "#10b981",
    },
    legendColorPurple: {
      backgroundColor: "#8b5cf6",
    },
    legendColorOrange: {
      backgroundColor: "#f59e0b",
    },
    legendLabel: {
      flex: 1,
      fontSize: "0.875rem",
      color: "#4b5563",
    },
    legendValue: {
      fontSize: "0.875rem",
      fontWeight: 500,
      color: "#111827",
      marginLeft: "0.5rem",
    },

    // Activity List
    activityCard: {
      backgroundColor: "#ffffff",
      borderRadius: "0.5rem",
      boxShadow:
        "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
      padding: "1.5rem",
      gridColumn: "span 2",
    },
    activityList: {
      marginTop: "1rem",
    },
    activityItem: {
      display: "flex",
      alignItems: "center",
      padding: "1rem 0",
      borderBottom: "1px solid #e5e7eb",
    },
    activityItemLast: {
      borderBottom: "none",
    },
    activityIcon: {
      width: "2rem",
      height: "2rem",
      borderRadius: "0.375rem",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginRight: "1rem",
      flexShrink: 0,
    },
    activityIconCreate: {
      backgroundColor: "#dbeafe",
      color: "#1e40af",
    },
    activityIconUpdate: {
      backgroundColor: "#fef3c7",
      color: "#92400e",
    },
    activityIconDelete: {
      backgroundColor: "#fee2e2",
      color: "#991b1b",
    },
    activityContent: {
      flex: 1,
    },
    activityTitle: {
      fontSize: "0.875rem",
      fontWeight: 500,
      color: "#111827",
      marginBottom: "0.25rem",
    },
    activityMeta: {
      fontSize: "0.75rem",
      color: "#6b7280",
    },
    activityTime: {
      fontSize: "0.75rem",
      color: "#9ca3af",
      marginLeft: "1rem",
    },

    // Quick Actions
    quickActionsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(4, 1fr)",
      gap: "1rem",
      marginTop: "1rem",
      marginBottom: "2rem",
    },
    quickActionCard: {
      padding: "1rem",
      backgroundColor: "#f9fafb",
      borderRadius: "0.5rem",
      textAlign: "center",
      cursor: "pointer",
      transition: "all 0.2s",
      textDecoration: "none",
      display: "block",
    },
    quickActionCardHover: {
      backgroundColor: "#f3f4f6",
      transform: "translateY(-2px)",
    },
    quickActionIcon: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: "0.5rem",
      color: "#2563eb",
    },
    quickActionTitle: {
      fontSize: "0.875rem",
      fontWeight: 500,
      color: "#111827",
      marginBottom: "0.25rem",
    },
    quickActionDescription: {
      fontSize: "0.75rem",
      color: "#6b7280",
    },

    // Loading Overlay
    loadingOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(255, 255, 255, 0.75)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
    },

    // Time range selector
    timeRangeSelector: {
      display: "flex",
      gap: "0.5rem",
      marginBottom: "1rem",
    },
    timeRangeButton: {
      padding: "0.375rem 1rem",
      fontSize: "0.875rem",
      fontWeight: 500,
      backgroundColor: "#f3f4f6",
      color: "#4b5563",
      border: "none",
      borderRadius: "0.375rem",
      cursor: "pointer",
      transition: "all 0.2s",
    },
    timeRangeButtonActive: {
      backgroundColor: "#2563eb",
      color: "#ffffff",
    },
    timeRangeButtonHover: {
      backgroundColor: "#e5e7eb",
    },

    // Welcome section
    welcomeSection: {
      backgroundColor: "#2563eb",
      borderRadius: "0.5rem",
      padding: "2rem",
      marginBottom: "2rem",
      color: "#ffffff",
    },
    welcomeTitle: {
      fontSize: "1.5rem",
      fontWeight: 700,
      marginBottom: "0.5rem",
    },
    welcomeText: {
      fontSize: "1rem",
      opacity: 0.9,
    },

    // Responsive
    "@media (max-width: 768px)": {
      chartsGrid: {
        gridTemplateColumns: "1fr",
      },
      quickActionsGrid: {
        gridTemplateColumns: "repeat(2, 1fr)",
      },
    },
  };

  useEffect(() => {
    dispatch(fetchUserStats());
    dispatch(fetchRoles({ limit: 100 }));
    dispatch(fetchUsers({ limit: 5, sortBy: "createdAt", sortOrder: "desc" }));
  }, [dispatch]);

  // Get icon color based on stat type
  const getStatIconStyle = (index) => {
    const colors = [
      styles.statIconBlue,
      styles.statIconGreen,
      styles.statIconPurple,
      styles.statIconOrange,
      styles.statIconRed,
      styles.statIconIndigo,
      styles.statIconTeal,
      styles.statIconPink,
    ];
    return colors[index % colors.length];
  };

  // Get bar color based on index
  const getBarColor = (index) => {
    const colors = [
      styles.barFillBlue,
      styles.barFillGreen,
      styles.barFillPurple,
      styles.barFillOrange,
      styles.barFillRed,
    ];
    return colors[index % colors.length];
  };

  // Stat cards data
  const statCards = [
    {
      title: "Utilisateurs Total",
      value: stats?.totalUsers || 0,
      icon: Users,
      change: "+12%",
      positive: true,
      link: "/admin/users",
    },
    {
      title: "Utilisateurs Actifs",
      value: stats?.activeUsers || 0,
      icon: UserCheck,
      change: "+8%",
      positive: true,
      link: "/admin/users?isActive=true",
    },
    {
      title: "Nouveaux Ces jours",
      value: stats?.newUsersThisWeek || 0,
      icon: Calendar,
      change: "+23%",
      positive: true,
      link: "/admin/users",
    },
    {
      title: "Nouveaux Ce Mois",
      value: stats?.newUsersThisMonth || 0,
      icon: CalendarDays,
      change: "+15%",
      positive: true,
      link: "/admin/users",
    },
    {
      title: "Connexions Récentes",
      value: stats?.recentLogins || 0,
      icon: Key,
      change: "-5%",
      positive: false,
      link: "/admin/activity",
    },
    {
      title: "Rôles Disponibles",
      value: roles?.length || 0,
      icon: Shield,
      change: "0%",
      positive: true,
      link: "/admin/roles",
    },
    {
      title: "Utilisateurs Vérifiés",
      value: stats?.verifiedUsers || 0,
      icon: CheckCircle2,
      change: "+18%",
      positive: true,
      link: "/admin/users",
    },
    {
      title: "Elements en attente",
      value: stats?.unverifiedUsers || 0,
      icon: Clock,
      change: "-2%",
      positive: true,
      link: "/admin/users?verified=false",
    },
  ];

  // Department distribution data
  const departmentData = stats?.usersByDepartment || [
    { department: "IT", count: 45 },
    { department: "Commercial", count: 30 },
    { department: "Marketing", count: 20 },
    { department: "HR", count: 15 },
    { department: "Finance", count: 12 },
  ];

  // Role distribution data
  const roleData = stats?.usersByRole || [
    { role: "Admin", count: 5 },
    { role: "Manager", count: 15 },
    { role: "User", count: 80 },
    { role: "Guest", count: 22 },
  ];

  // Activity data (mock)
  const recentActivity = [
    {
      id: 1,
      type: "create",
      title: "New user created",
      description: "John Doe was added to the system",
      time: "5 minutes ago",
    },
    {
      id: 2,
      type: "update",
      title: "User updated",
      description: "Jane Smith's role was changed to Manager",
      time: "1 hour ago",
    },
    {
      id: 3,
      type: "delete",
      title: "User deleted",
      description: "Inactive user account removed",
      time: "3 hours ago",
    },
    {
      id: 4,
      type: "create",
      title: "New role created",
      description: "Supervisor role was added",
      time: "5 hours ago",
    },
  ];

  // Quick actions
  const quickActions = [
    {
      icon: UserPlus,
      title: "Utilisateur",
      description: "Creer un nouvel utilisateur",
      link: "/admin/users/form",
    },
    {
      icon: Shield,
      title: "Creer un Rôle",
      description: "Ajouter un nouveau rôle",
      link: "/admin/roles/new",
    },
    {
      icon: KeyRound,
      title: "Permissions",
      description: "Gérer les permissions",
      link: "/admin/permissions",
    },
    {
      icon: BarChart3,
      title: "Voir les rapports",
      description: "Analytics & reports",
      link: "/admin/analytics",
    },
  ];

  // Get activity icon style based on type
  const getActivityIconStyle = (type) => {
    switch (type) {
      case "create":
        return styles.activityIconCreate;
      case "update":
        return styles.activityIconUpdate;
      case "delete":
        return styles.activityIconDelete;
      default:
        return styles.activityIconCreate;
    }
  };

  // Get activity icon based on type
  const getActivityIcon = (type) => {
    switch (type) {
      case "create":
        return PlusCircle;
      case "update":
        return Edit;
      case "delete":
        return Trash2;
      default:
        return ClipboardList;
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingOverlay}>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      {/* <div style={styles.header}>
        <h1 style={styles.title}>Dashboard</h1>
        <p style={styles.subtitle}>
          Welcome back! Here's what's happening with your application.
        </p>
      </div> */}

      {/* Welcome Section */}
      <div style={styles.welcomeSection}>
        <h2 style={styles.welcomeTitle}>
          Bon {new Date().getHours() < 12 ? "Jour" : "Après-midi"}!
        </h2>
        <p style={styles.welcomeText}>
          Vous avez {stats?.pendingClaims || 0} sinistres en attente et{" "}
          {stats?.activeUsers || 0} utilisateurs actifs.
        </p>
      </div>

      {/* Time Range Selector */}
      <div style={styles.timeRangeSelector}>
        <button
          style={{
            ...styles.timeRangeButton,
            ...(timeRange === "day" ? styles.timeRangeButtonActive : {}),
          }}
          onClick={() => setTimeRange("day")}
          onMouseEnter={(e) => {
            if (timeRange !== "day") {
              e.currentTarget.style.backgroundColor =
                styles.timeRangeButtonHover.backgroundColor;
            }
          }}
          onMouseLeave={(e) => {
            if (timeRange !== "day") {
              e.currentTarget.style.backgroundColor =
                styles.timeRangeButton.backgroundColor;
            }
          }}
        ></button>
        <button
          style={{
            ...styles.timeRangeButton,
            ...(timeRange === "week" ? styles.timeRangeButtonActive : {}),
          }}
          onClick={() => setTimeRange("week")}
          onMouseEnter={(e) => {
            if (timeRange !== "week") {
              e.currentTarget.style.backgroundColor =
                styles.timeRangeButtonHover.backgroundColor;
            }
          }}
          onMouseLeave={(e) => {
            if (timeRange !== "week") {
              e.currentTarget.style.backgroundColor =
                styles.timeRangeButton.backgroundColor;
            }
          }}
        >
          Cette Semaine
        </button>
        <button
          style={{
            ...styles.timeRangeButton,
            ...(timeRange === "month" ? styles.timeRangeButtonActive : {}),
          }}
          onClick={() => setTimeRange("month")}
          onMouseEnter={(e) => {
            if (timeRange !== "month") {
              e.currentTarget.style.backgroundColor =
                styles.timeRangeButtonHover.backgroundColor;
            }
          }}
          onMouseLeave={(e) => {
            if (timeRange !== "month") {
              e.currentTarget.style.backgroundColor =
                styles.timeRangeButton.backgroundColor;
            }
          }}
        >
          Ce Mois
        </button>
        <button
          style={{
            ...styles.timeRangeButton,
            ...(timeRange === "year" ? styles.timeRangeButtonActive : {}),
          }}
          onClick={() => setTimeRange("year")}
          onMouseEnter={(e) => {
            if (timeRange !== "year") {
              e.currentTarget.style.backgroundColor =
                styles.timeRangeButtonHover.backgroundColor;
            }
          }}
          onMouseLeave={(e) => {
            if (timeRange !== "year") {
              e.currentTarget.style.backgroundColor =
                styles.timeRangeButton.backgroundColor;
            }
          }}
        >
          Cette Année
        </button>
      </div>

      {error && <Alert type="error" message={error} />}

      {/* Stats Grid */}
      <div style={styles.statsGrid}>
        {statCards.map((card, index) => (
          <Link
            key={card.title}
            to={card.link}
            style={styles.statCard}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = styles.statCardHover.transform;
              e.currentTarget.style.boxShadow = styles.statCardHover.boxShadow;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "none";
              e.currentTarget.style.boxShadow =
                "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)";
            }}
          >
            <div style={styles.statContent}>
              <div style={{ ...styles.statIcon, ...getStatIconStyle(index) }}>
                <card.icon size={24} strokeWidth={2} />
              </div>
              <div style={styles.statInfo}>
                <div style={styles.statLabel}>{card.title}</div>
                <div style={styles.statValue}>
                  {card.value.toLocaleString()}
                </div>
                {card.change && (
                  <div
                    style={{
                      ...styles.statChange,
                      ...(card.positive
                        ? styles.statChangePositive
                        : styles.statChangeNegative),
                    }}
                  >
                    {card.change} de la précédente
                  </div>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Charts Grid */}
      <div style={styles.chartsGrid}>
        {/* Department Distribution */}
        <div style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <h3 style={styles.chartTitle}>Utilisateurs par Département</h3>
            <div style={styles.chartControls}>
              <button
                style={{
                  ...styles.chartControlButton,
                  ...(selectedChart === "department"
                    ? styles.chartControlButtonActive
                    : {}),
                }}
                onClick={() => setSelectedChart("department")}
              >
                Bar
              </button>
              <button
                style={{
                  ...styles.chartControlButton,
                  ...(selectedChart === "department-pie"
                    ? styles.chartControlButtonActive
                    : {}),
                }}
                onClick={() => setSelectedChart("department-pie")}
              >
                Pie
              </button>
            </div>
          </div>

          {selectedChart === "department" ? (
            <div style={styles.barChart}>
              {departmentData.map((dept, index) => {
                const max = Math.max(...departmentData.map((d) => d.count));
                const percentage = (dept.count / max) * 100;
                return (
                  <div key={dept.department} style={styles.barItem}>
                    <div style={styles.barLabel}>
                      <span style={styles.barLabelText}>{dept.department}</span>
                      <span style={styles.barLabelValue}>{dept.count}</span>
                    </div>
                    <div style={styles.barContainer}>
                      <div
                        style={{
                          ...styles.barFill,
                          ...getBarColor(index),
                          width: `${percentage}%`,
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={styles.pieChartContainer}>
              <div style={styles.pieChart}>
                <div style={styles.pieChartCenter}>
                  {departmentData.reduce((sum, dept) => sum + dept.count, 0)}
                </div>
              </div>
              <div style={styles.pieLegend}>
                {departmentData.slice(0, 4).map((dept, index) => (
                  <div key={dept.department} style={styles.legendItem}>
                    <div
                      style={{ ...styles.legendColor, ...getBarColor(index) }}
                    />
                    <span style={styles.legendLabel}>{dept.department}</span>
                    <span style={styles.legendValue}>{dept.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Role Distribution */}
        <div style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <h3 style={styles.chartTitle}>Utilisateurs par Rôle</h3>
            <div style={styles.chartControls}>
              <button
                style={{
                  ...styles.chartControlButton,
                  ...(selectedChart === "role"
                    ? styles.chartControlButtonActive
                    : {}),
                }}
                onClick={() => setSelectedChart("role")}
              >
                Bar
              </button>
              <button
                style={{
                  ...styles.chartControlButton,
                  ...(selectedChart === "role-pie"
                    ? styles.chartControlButtonActive
                    : {}),
                }}
                onClick={() => setSelectedChart("role-pie")}
              >
                Pie
              </button>
            </div>
          </div>

          {selectedChart === "role" ? (
            <div style={styles.barChart}>
              {roleData.map((role, index) => {
                const max = Math.max(...roleData.map((r) => r.count));
                const percentage = (role.count / max) * 100;
                return (
                  <div key={role.role} style={styles.barItem}>
                    <div style={styles.barLabel}>
                      <span style={styles.barLabelText}>{role.role}</span>
                      <span style={styles.barLabelValue}>{role.count}</span>
                    </div>
                    <div style={styles.barContainer}>
                      <div
                        style={{
                          ...styles.barFill,
                          ...getBarColor(index + 2),
                          width: `${percentage}%`,
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={styles.pieChartContainer}>
              <div style={styles.pieChart}>
                <div style={styles.pieChartCenter}>
                  {roleData.reduce((sum, role) => sum + role.count, 0)}
                </div>
              </div>
              <div style={styles.pieLegend}>
                {roleData.slice(0, 4).map((role, index) => (
                  <div key={role.role} style={styles.legendItem}>
                    <div
                      style={{
                        ...styles.legendColor,
                        ...getBarColor(index + 2),
                      }}
                    />
                    <span style={styles.legendLabel}>{role.role}</span>
                    <span style={styles.legendValue}>{role.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div style={styles.activityCard}>
          <h3 style={styles.chartTitle}>Activité Récente</h3>
          <div style={styles.activityList}>
            {recentActivity.map((activity, index) => (
              <div
                key={activity.id}
                style={{
                  ...styles.activityItem,
                  ...(index === recentActivity.length - 1
                    ? styles.activityItemLast
                    : {}),
                }}
              >
                <div
                  style={{
                    ...styles.activityIcon,
                    ...getActivityIconStyle(activity.type),
                  }}
                >
                  {React.createElement(getActivityIcon(activity.type), {
                    size: 16,
                    strokeWidth: 2,
                  })}
                </div>
                <div style={styles.activityContent}>
                  <div style={styles.activityTitle}>{activity.title}</div>
                  <div style={styles.activityMeta}>{activity.description}</div>
                </div>
                <div style={styles.activityTime}>{activity.time}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div style={styles.quickActionsGrid}>
        {quickActions.map((action) => (
          <Link
            key={action.title}
            to={action.link}
            style={styles.quickActionCard}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor =
                styles.quickActionCardHover.backgroundColor;
              e.currentTarget.style.transform =
                styles.quickActionCardHover.transform;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor =
                styles.quickActionCard.backgroundColor;
              e.currentTarget.style.transform = "none";
            }}
          >
            <div style={styles.quickActionIcon}>
              <action.icon size={24} strokeWidth={2} />
            </div>
            <div style={styles.quickActionTitle}>{action.title}</div>
            <div style={styles.quickActionDescription}>
              {action.description}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default Dashboard;
